/**
 * @module Reducers.AcademySetup
 *
 */
import { RequestError } from "typedefs";
import { AcademySetupAction } from "app/actions/academySetup";
import {
  AcademySetupCompletion,
  AcademySetupOptionalStates,
  AcademySetupCta,
  AcademySetupItemKey,
} from "app/components/AcademySetup/items";

export type AcademySetupState = {
  /**
   * Account the slice was fetched for. Sign-out is an in-app navigation, so
   * the store survives it; a response for another account replaces the
   * cached state instead of merging into it.
   */
  ownerId: string | null;
  /** null until the first fetch resolves — consumers hide until then. */
  data: AcademySetupCompletion | null;
  /** Visited/skipped state of the optional items; empty until fetched. */
  optionalStates: AcademySetupOptionalStates;
  /**
   * Optional marks applied optimistically whose PUT has not resolved. The
   * rows complete at once; the one-time completion celebration waits until
   * this is empty, so a write that then fails cannot have burned it.
   */
  pendingOptional: Partial<Record<AcademySetupItemKey, true>>;
  /**
   * CTA that launched each item, kept until that item's flag lands so the
   * completion can be attributed to it. Per item: confirmation polling can
   * outlive the page (the invitation flow polls ~15 s), so a second item
   * started meanwhile must not take the first one's attribution.
   */
  ctas: Partial<Record<AcademySetupItemKey, AcademySetupCta>>;
  error: RequestError | null;
  pending: boolean;
};

const initialState: AcademySetupState = {
  ownerId: null,
  data: null,
  optionalStates: {},
  pendingOptional: {},
  ctas: {},
  error: null,
  pending: false,
};

export default function academySetupReducer(
  state = initialState,
  action: AcademySetupAction,
): AcademySetupState {
  switch (action.type) {
    case "@academySetup.fetch.pending":
      // A fetch for another account than the cached slice starts that
      // account from nothing (sign-out is an in-app navigation, so the store
      // survives it); its own error and completion then read as its own.
      if (state.ownerId !== action.payload) {
        return { ...initialState, ownerId: action.payload, pending: true };
      }
      return { ...state, error: null, pending: true };
    case "@academySetup.fetch.success": {
      const sameOwner = state.ownerId === action.payload.ownerId;
      return {
        ...state,
        ownerId: action.payload.ownerId,
        data: action.payload.data,
        // Union, server winning: a mark made this session survives the
        // refetch each landing page triggers on mount (which can land before
        // the PUT that persists the mark), and states are only ever added,
        // never cleared — within one account. Another account's response
        // starts from its own states.
        optionalStates: sameOwner
          ? { ...state.optionalStates, ...action.payload.optionalStates }
          : action.payload.optionalStates,
        pendingOptional: sameOwner ? state.pendingOptional : {},
        // Consumed as each item's flag lands; another account starts clean.
        ctas: sameOwner
          ? Object.fromEntries(
              Object.entries(state.ctas).filter(
                ([key]) => !action.payload.data[key as AcademySetupItemKey],
              ),
            )
          : {},
        pending: false,
      };
    }
    case "@academySetup.fetch.error":
      return { ...state, error: action.payload, pending: false };
    case "@academySetup.cta.clicked":
      return {
        ...state,
        ctas: { ...state.ctas, [action.payload.key]: action.payload },
      };
    case "@academySetup.optional.marked":
      return {
        ...state,
        optionalStates: {
          ...state.optionalStates,
          [action.payload.key]: action.payload.state,
        },
        pendingOptional: { ...state.pendingOptional, [action.payload.key]: true },
      };
    case "@academySetup.optional.confirmed": {
      const pendingOptional = { ...state.pendingOptional };
      delete pendingOptional[action.payload];
      return { ...state, pendingOptional };
    }
    case "@academySetup.optional.reverted": {
      const optionalStates = { ...state.optionalStates };
      if (action.payload.previous === undefined) {
        delete optionalStates[action.payload.key];
      } else {
        optionalStates[action.payload.key] = action.payload.previous;
      }
      const pendingOptional = { ...state.pendingOptional };
      delete pendingOptional[action.payload.key];
      const ctas = { ...state.ctas };
      if (action.payload.dropCta) {
        delete ctas[action.payload.key];
      }
      return { ...state, optionalStates, pendingOptional, ctas };
    }

    default:
      return state;
  }
}
